<template>
  <div
    v-if="!isAppoinmentMode"
    class="status-guide order-3 order-lg-1 mt-3 mt-lg-1"
    role="group"
    aria-labelledby="dashboard-filters-old-label"
  >
    <div
      id="dashboard-filters-old-label"
      class="me-3"
      role="heading"
      aria-level="3"
    >
      Filters:
    </div>
    <div class="status-list">
      <CCheckBox
        :disabled="activePassState === 'ended'"
        label="Waiting Approval"
        :model-value="localFilterData.waiting_approval"
        name="waiting_approval"
        :inline="true"
        :custom="true"
        class="custom-checkbox edited"
        @change="updateFilters"
      />
      <CCheckBox
        v-if="passesTimeSettings && passesTimeSettings.min_time"
        :label="
          $helpers.formatDate(passesTimeSettings.min_time, 'hh:mm:ss', 'mm') +
          '+Min'
        "
        :model-value="localFilterData.yellow_time"
        name="yellow_time"
        :inline="true"
        :custom="true"
        class="custom-checkbox min-10"
        @change="updateFilters"
      />
      <CCheckBox
        :disabled="activePassState === 'active'"
        label="System Ended"
        :model-value="localFilterData.system_ended"
        name="system_ended"
        :inline="true"
        :custom="true"
        class="custom-checkbox system-ended"
        @change="updateFilters"
      />
      <CCheckBox
        :disabled="activePassState === 'active'"
        label="Extended Passes"
        :model-value="localFilterData.extended_time"
        name="extended_time"
        :inline="true"
        :custom="true"
        class="custom-checkbox extended-passes"
        @change="updateFilters"
      />
      <CCheckBox
        :disabled="activePassState === 'active'"
        label="Edited"
        :model-value="localFilterData.edited"
        name="edited"
        :inline="true"
        :custom="true"
        class="custom-checkbox gray"
        @change="updateFilters"
      />
      <CCheckBox
        label="Student Number"
        :model-value="localFilterData.student_sis_id"
        name="student_sis_id"
        class="custom-checkbox"
        @change="updateFilters"
        :inline="true"
        :custom="true"
      />
      <template v-if="isTardyAllowedForCurrentUser">
        <CCheckBox
          label="Late To Class"
          :model-value="localFilterData.late_to_class"
          name="late_to_class"
          class="custom-checkbox"
          @change="updateFilters"
          :inline="true"
          :custom="true"
        />

        <CCheckBox
          label="Late To School"
          :model-value="localFilterData.late_to_school"
          name="late_to_school"
          class="custom-checkbox"
          @change="updateFilters"
          :inline="true"
          :custom="true"
        />
      </template>
      <CCheckBox
        label="Limit Exceeded"
        :model-value="localFilterData.limit_exceeded"
        name="limit_exceeded"
        class="custom-checkbox"
        @change="updateFilters"
        :inline="true"
        :custom="true"
      />
      <CCheckBox
        :disabled="activePassState === 'ended'"
        label="Hallway"
        :model-value="localFilterData.hallway"
        name="hallway"
        class="custom-checkbox hallway-filter"
        @change="updateFilters"
        :inline="true"
        :custom="true"
      />
    </div>
  </div>
</template>

<script>
import { computed, ref, watch } from "vue"
import { useStore } from "vuex"

export default {
  name: "DashboardTimeFiltersOld",
  setup() {
    const store = useStore()

    const localFilterData = ref({
      waiting_approval: false,
      yellow_time: false,
      system_ended: false,
      extended_time: false,
      edited: false,
      student_sis_id: false,
      late_to_class: false,
      late_to_school: false,
      limit_exceeded: false,
      hallway: false
    })

    const isActiveAppoinmentModule = computed(
      () => store.getters["schools/isActiveAppoinmentModule"]
    )

    const passesTimeSettings = computed(
      () => store.getters["passes/passesTimeSettings"]
    )

    const activeSchoolHasAppoitmentPasses = computed(
      () => store.getters["schools/activeSchoolHasAppoitmentPasses"]
    )

    const filterData = computed(
      () => store.getters["dashboardTable/filterData"]
    )

    const activePassState = computed(
      () => store.getters["dashboardTable/getStatus"]
    )

    const activePassType = computed(
      () => store.getters["dashboardTable/getType"]
    )

    const isAppoinmentMode = computed(() => {
      return activePassType.value.includes("appointment")
    })

    const userRole = computed(() => store.getters["authentication/getUserRole"])
    const tardyOptions = computed(
      () => store.getters["schools/tardyManagementOptions"]
    )
    const isTardyAllowedForAdmin = computed(
      () => store.getters["schools/isTardyAllowedForAdmin"]
    )
    const user = computed(() => store.getters["authentication/user"])

    const isTardyAllowedForCurrentUser = computed(() => {
      if (!tardyOptions.value || !tardyOptions.value?.usage?.["within-ehp"]) {
        return false
      }
      const allowTeacherStaff = Boolean(
        tardyOptions.value?.["allow-teacher-staff"]
      )
      if (
        allowTeacherStaff &&
        ["admin", "staff", "teacher"].includes(userRole.value)
      ) {
        return true
      }
      if (userRole.value === "admin") {
        return isTardyAllowedForAdmin.value
      }
      const allowSelectedUsersOnly =
        tardyOptions.value?.["allow-selected-users-only"] || false
      if (user.value?.allow_tardy && allowSelectedUsersOnly) {
        return true
      }
      return false
    })

    const updateFilters = (event) => {
      store.commit("dashboardTable/UPDATE_FILTERS_DATA", {
        filter: event.target.name,
        value: event.target.checked
      })
    }

    watch(
      () => filterData.value,
      (newVal) => {
        localFilterData.value = newVal
      },
      {
        deep: true
      }
    )

    return {
      isActiveAppoinmentModule,
      isTardyAllowedForCurrentUser,
      passesTimeSettings,
      activeSchoolHasAppoitmentPasses,
      localFilterData,
      activePassState,
      activePassType,
      isAppoinmentMode,
      updateFilters
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/assets/scss/_variables.scss";

.status-list :deep(.form-check.custom-checkbox) {
  border-radius: 4px;
}

.status-list :deep(.form-check.custom-checkbox:focus-within) {
  outline: 2px solid $outline-color;
  outline-offset: $outline-offset;
}

.status-list
  :deep(
    .form-check.custom-checkbox
      .form-check-input:focus-visible
      ~ .form-check-label
  ) {
  text-decoration: underline;
  text-underline-offset: 2px;
}
</style>
